import React, { useMemo } from 'react';
import { Button } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { ListBase, ListProps, useListContext, usePermissions, useRefresh } from 'react-admin';
import { Link } from 'react-router-dom';
import { InfiniteFoot, useScrollBatch } from '../DataList';
import MainCard from '../MainCard';
import RoutableTabs from '../RoutableTabs';
import { RoutableTabConfig } from '@src/types';
import { IntegrationRow } from './IntegrationItem';
import ResponsesTable from './ResponsesTable';
import { IntegrationConfig } from './types';
import './Integrations.css';

/**
 * The webhook and healthcheck list endpoints have no filterset_class on the
 * API, so paging them server-side would give the user no way to narrow the
 * set. Both are configuration objects bounded by the number of services, so
 * the page fetches them in one go and lets the reader scroll through them. If a
 * tenant ever exceeds this the footer note says so rather than quietly hiding
 * records.
 */
const FETCH_LIMIT = 500;
/** how many more rows come into the list each time the reader reaches the end */
const BATCH = 12;
const SKELETON_COUNT = 6;

const IntegrationCollection = ({ config }: { config: IntegrationConfig }) => {
    const { ids, data, loading, total } = useListContext();
    const refresh = useRefresh();
    const { permissions } = usePermissions();

    const records: any[] = useMemo(() => ids.map((id) => data[id]).filter(Boolean), [ids, data]);

    // every record is here already, so what arrives on scroll is not a page but
    // more of what the page is holding
    const { shown, hasMore, sentinelRef } = useScrollBatch({ total: records.length, batch: BATCH });
    const pageItems = records.slice(0, shown);
    const canWrite = config.canWrite(permissions);

    // a background refetch keeps `loading` true — only fall back to skeletons
    // when there is nothing on screen yet
    if (loading && records.length === 0) {
        return (
            <div className="nio-skeletons">
                {Array.from({ length: SKELETON_COUNT }).map((_, index) => (
                    <div key={index} className="nio-skel" />
                ))}
            </div>
        );
    }

    if (records.length === 0) {
        return (
            <div className="nio-empty">
                <div className="nio-empty__title">{config.emptyTitle}</div>
                <p className="nio-empty__body">{config.emptyBody}</p>
                {canWrite && (
                    <Link to={`${config.basePath}/create`}>
                        <Button type="primary" icon={<PlusOutlined />}>
                            {config.createLabel}
                        </Button>
                    </Link>
                )}
            </div>
        );
    }

    return (
        <>
            {/* `each` rather than `stagger`: the list grows under the reader, so
                only the rows just appended mount and animate */}
            <div className="nio-list nx-enter-each">
                {pageItems.map((record) => (
                    <IntegrationRow key={record.id} record={record} config={config} refresh={refresh} />
                ))}
            </div>

            {(total ?? 0) > records.length && (
                <div className="nio-pagination">
                    <span className="nio-pagination__note">
                        Only the first {records.length} of {total} were loaded.
                    </span>
                </div>
            )}

            <InfiniteFoot
                sentinelRef={sentinelRef}
                loading={false}
                loaded={pageItems.length}
                total={records.length}
                hasMore={hasMore}
                noun={config.singular.toLowerCase()}
                plural={config.plural.toLowerCase()}
            />
        </>
    );
};

const IntegrationListPage = ({ config, listProps }: { config: IntegrationConfig; listProps: ListProps }) => {
    const { permissions } = usePermissions();
    const canWrite = config.canWrite(permissions);

    // keeps the ?tab= value readable, and matches the keys the old pages used
    const itemsTabKey = config.plural.toLowerCase();

    const tabs: RoutableTabConfig[] = [
        {
            key: itemsTabKey,
            tab: <span>{config.plural}</span>,
            content: (
                <ListBase {...listProps} perPage={FETCH_LIMIT}>
                    <IntegrationCollection config={config} />
                </ListBase>
            ),
        },
        {
            key: 'responses',
            tab: <span>Responses</span>,
            content: <ResponsesTable config={config.responses} />,
        },
    ];

    return (
        <div className="nio netorca-frame">
            <MainCard className="nio-panel">
                <RoutableTabs
                    className="nio-tabs"
                    tabs={tabs}
                    defaultActiveKey={itemsTabKey}
                    // creating one belongs to the page, not to either tab, so it
                    // closes the tab strip rather than taking a header above it
                    tabBarExtraContent={canWrite ? (
                        <Link to={`${config.basePath}/create`}>
                            <Button className="nio-create" type="primary" size="small" icon={<PlusOutlined />}>
                                {config.createLabel}
                            </Button>
                        </Link>
                    ) : undefined}
                />
            </MainCard>
        </div>
    );
};

export default IntegrationListPage;
